import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2, RotateCcw } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export interface WarehouseType {
  id: string;
  name: string;
  description?: string;
  isDefault?: boolean;
}

const DEFAULT_TYPES: WarehouseType[] = [
  { id: "none", name: "None", isDefault: true },
  { id: "main", name: "Main", isDefault: true },
  { id: "satellite", name: "Satellite", isDefault: true },
  { id: "retail", name: "Retail", isDefault: true },
  { id: "storage", name: "Storage", isDefault: true },
  { id: "distribution", name: "Distribution", isDefault: true },
];

const STORAGE_KEY = "warehouse-types";

export const getWarehouseTypes = (): WarehouseType[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const types = JSON.parse(stored);
      
      // Migration: Add "None" if it doesn't exist
      const hasNone = types.some((t: WarehouseType) => t.name === "None");
      if (!hasNone) {
        const noneType = { id: "none", name: "None", isDefault: true };
        const updatedTypes = [noneType, ...types];
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedTypes));
        return updatedTypes;
      }
      
      return types;
    }
  } catch (error) {
    console.error("Error loading warehouse types:", error);
  }
  return DEFAULT_TYPES;
};

const saveWarehouseTypes = (types: WarehouseType[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(types));
  } catch (error) {
    console.error("Error saving warehouse types:", error);
  }
};

interface WarehouseTypesSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WarehouseTypesSettingsDialog({
  open,
  onOpenChange,
}: WarehouseTypesSettingsDialogProps) {
  const [types, setTypes] = useState<WarehouseType[]>([]);
  const [editingType, setEditingType] = useState<WarehouseType | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [formData, setFormData] = useState({ name: "", description: "" });

  useEffect(() => {
    if (open) {
      setTypes(getWarehouseTypes());
    }
  }, [open]);

  const handleSave = () => {
    if (!formData.name.trim()) return;

    const newType: WarehouseType = {
      id: editingType?.id || `custom-${Date.now()}`,
      name: formData.name.trim(),
      description: formData.description.trim(),
      isDefault: editingType?.isDefault || false,
    };

    let updatedTypes: WarehouseType[];
    if (editingType) {
      updatedTypes = types.map((t) => (t.id === editingType.id ? newType : t));
    } else {
      updatedTypes = [...types, newType];
    }

    setTypes(updatedTypes);
    saveWarehouseTypes(updatedTypes);
    handleCancel();
  };

  const handleDelete = (id: string) => {
    const updatedTypes = types.filter((t) => t.id !== id);
    setTypes(updatedTypes);
    saveWarehouseTypes(updatedTypes);
  };

  const handleEdit = (type: WarehouseType) => {
    setEditingType(type);
    setFormData({ name: type.name, description: type.description || "" });
    setIsAddingNew(false);
  };

  const handleCancel = () => {
    setEditingType(null);
    setIsAddingNew(false);
    setFormData({ name: "", description: "" });
  };

  const handleResetToDefaults = () => {
    setTypes(DEFAULT_TYPES);
    saveWarehouseTypes(DEFAULT_TYPES);
    handleCancel();
  };

  const showForm = isAddingNew || editingType;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Warehouse Types Settings</DialogTitle>
          <DialogDescription>
            Manage warehouse types that can be used when creating warehouses.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!showForm && (
            <div className="flex justify-between items-center">
              <Button
                onClick={() => {
                  setIsAddingNew(true);
                  setFormData({ name: "", description: "" });
                }}
                size="sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Type
              </Button>
              <Button
                onClick={handleResetToDefaults}
                variant="outline"
                size="sm"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset to Defaults
              </Button>
            </div>
          )}

          {showForm && (
            <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
              <div className="space-y-2">
                <Label htmlFor="type-name">
                  Type Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="type-name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g., Cold Storage"
                  disabled={editingType?.isDefault}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type-description">Description</Label>
                <Textarea
                  id="type-description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Optional description for this warehouse type"
                  rows={3}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSave} size="sm">
                  {editingType ? "Update" : "Add"}
                </Button>
                <Button onClick={handleCancel} variant="outline" size="sm">
                  Cancel
                </Button>
              </div>
            </div>
          )}

          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {types.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted-foreground">
                      No warehouse types defined
                    </TableCell>
                  </TableRow>
                ) : (
                  types.map((type) => (
                    <TableRow key={type.id}>
                      <TableCell className="font-medium">
                        {type.name}
                        {type.isDefault && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            (Default)
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {type.description || "-"}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(type)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {!type.isDefault && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(type.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
